import moment from 'moment'
import sendOnChange from '@/utils/send-on-change'
import { isComputedSensor, computedPair } from '@/utils/computed-sensors'

//
// Monitoring health-status logic, extracted from HomePage.vue so the
// dashboard status tiles judge facilities/controllers with EXACTLY the
// same rules as the monitoring view (HomePage delegates to this module).
//
// All functions are pure except for the sticky send-on-change map below.
// Time-dependent checks accept an options object `{ seenMap, now }`:
//   seenMap  sticky per-session map "sensor id -> vt:36 was observed"
//            (defaults to the shared singleton below)
//   now      timestamp in ms for staleness checks (defaults to moment())
//
// NOTE: sensorAlertStatus() and severityForReason() intentionally keep
// their historical, slightly divergent mappings — do not "fix" one to
// match the other, the monitoring view depends on both as they are.
//

export const AlertReason = {
  NONE: 0,
  DEVICE_DISABLED: 1,
  DEVICE_CONTACT: 2,
  STALE_DATA: 3,
  BATTERY_LOW: 4,
  USAGE_LIMIT: 5,
  SENSOR_WARNING: 6,
  SENSOR_ALERT: 7,
  SENSOR_DISABLED: 8,
  NO_SENSORDATA: 9,
  NO_SENSORDATA_DIRECT: 10,
  SENSOR_EVENT: 11,
  UNKNOWN: -1
}

export const AlertState = {
  OK: 0,
  INFO: 1,
  WARNING: 2,
  CRITICAL: 3,
  UNKNOWN: -1
}

// The monitoring severity palette (gradient stops in HomePage use the
// same rgba values verbatim).
export const SeverityColors = {
  ok: 'rgba(127,232,127,1)',
  info: 'rgba(255,181,3,1)',
  warning: 'rgba(255,128,0,1)',
  critical: 'rgba(228,0,0,1)',
  disabled: 'rgba(242,242,242,1)'
}

// severity: 0 = ok, 1 = info, 2 = warning, 3 = critical, 4 = disabled
export function colorForSeverity (severity) {
  switch (severity) {
    case 3: return SeverityColors.critical
    case 2: return SeverityColors.warning
    case 1: return SeverityColors.info
    case 4: return SeverityColors.disabled
    default: return SeverityColors.ok
  }
}

// i18n key for an AlertReason — same key space HomePage.alertReasonText
// resolves; kept here as a pure mapping so modals outside HomePage can
// translate reasons without duplicating the switch.
export function i18nKeyForReason (reason) {
  switch (reason) {
    case AlertReason.NONE: return 'sensorAlertReason.None'
    case AlertReason.DEVICE_DISABLED: return 'sensorAlertReason.DeviceIsDisabled'
    case AlertReason.DEVICE_CONTACT: return 'sensorAlertReason.NoContactToDevice'
    case AlertReason.STALE_DATA: return 'sensorAlertReason.StaleData'
    case AlertReason.BATTERY_LOW: return 'sensorAlertReason.BatteryIsLow'
    case AlertReason.USAGE_LIMIT: return 'sensorAlertReason.UsageLimitReached'
    case AlertReason.SENSOR_WARNING: return 'sensorAlertReason.SensorWarning'
    case AlertReason.SENSOR_ALERT: return 'sensorAlertReason.SensorAlert'
    case AlertReason.SENSOR_DISABLED: return 'sensorAlertReason.SensorIsDisbled'
    case AlertReason.NO_SENSORDATA: return 'sensorAlertReason.NoSensorData'
    case AlertReason.SENSOR_EVENT: return 'sensorAlertReason.EventWasTriggered'
    default: return 'sensorAlertReason.Unknown'
  }
}

//
// Sticky per-session map: sensor id -> "a vt:36 tuple was seen". Shared
// between the monitoring view and the dashboard status tiles so both
// reach the same staleness verdict for send-on-change (CS-* v5) sensors.
// Plain mutation is fine — consumers re-render on the 60-s data poll.
//
export const sendOnChangeSeen = {}

function resolveNow (opts) {
  return opts && opts.now != null ? opts.now : moment().valueOf()
}

function resolveSeenMap (opts) {
  return opts && opts.seenMap ? opts.seenMap : sendOnChangeSeen
}

// Geräte-Heartbeat: der CDC meldet sich alle ~5 Minuten. Als "ohne Kontakt"
// gilt ein Gerät nach zwei verpassten Heartbeats plus 2 Minuten Karenz —
// exakt 10 Minuten würde den zweiten Heartbeat knapp verpassen. Die Apps
// (iOS/Android) übernehmen denselben Wert (mobile-parity-plan.md).
export const DEVICE_HEARTBEAT_MINUTES = 5
export const DEVICE_CONTACT_GRACE_MINUTES = 2
export const DEVICE_CONTACT_THRESHOLD_MS = (2 * DEVICE_HEARTBEAT_MINUTES + DEVICE_CONTACT_GRACE_MINUTES) * 60 * 1000

export function isOutdated (timestamp, opts) {
  if (!timestamp) {
    return true
  }
  const delta = resolveNow(opts) - timestamp
  return (delta > DEVICE_CONTACT_THRESHOLD_MS)
}

export function maxLastSeen (facility) {
  let max = 0
  if (facility) {
    for (const device of facility.devices) {
      const lastseen = device.lastseen
      if (lastseen) {
        max = Math.max(max, moment(lastseen).valueOf())
      }
    }
  }
  return max
}

export function facilityIsDisabled (facility) {
  for (const device of facility.devices) {
    if (device.enabled) {
      return false
    }
  }
  return true
}

export function isVirtualSensor (sensor) {
  if (!sensor) return false
  // Robust detection: prefer the structural connector flag (6 = CDC_C_VIRTUAL,
  // set server-side at creation), fall back to model for older payloads where
  // connector was not exposed.
  if (sensor.connector === 6) return true
  return sensor.model === 'CS_Virtual'
}

export function dataPackageForSensor (sensor) {
  return sensor && sensor.data && sensor.data.length > 0 ? sensor.data[0] : null
}

export function recordedtime (sensor) {
  const data = sensor ? sensor.data : null
  if (data && data.length > 0) {
    const entry = data[0]
    if (entry && entry.recordedtime) {
      return entry.recordedtime
    }
  }
  return null
}

export function findSensorValueForSystemPartAndType (sensor, pair, datapackage) {
  if (sensor && datapackage) {
    for (const value of datapackage.values) {
      if (value.systempart === pair.systempart && value.type === pair.valuetype) {
        return value
      }
    }
  }
  return null
}

export function firstGlanceSystemPartAndType (sensorOrType) {
  // Accepts either a sensor object or a raw type code. The object form
  // is needed to detect virtual sensors, which only carry counter
  // readings (Wh / m³ totals) — not the per-phase / per-minute pulse
  // values the physical defaults below assume.
  let sensor = null
  let sensorType = null
  if (typeof sensorOrType === 'object' && sensorOrType !== null) {
    sensor = sensorOrType
    sensorType = sensor.type
  } else {
    sensorType = sensorOrType
  }

  // Computed sensors write exactly the reading they are configured for.
  if (sensor && isComputedSensor(sensor)) {
    return computedPair(sensor)
  }

  if (sensor && isVirtualSensor(sensor)) {
    switch (sensorType) {
      case 0: // Strom — Wh total, displayed as kWh
      case 3: // Wärme — Wh total, displayed as kWh
        return { systempart: 4, valuetype: 9 }
      case 1: // Gas — m³ total
      case 2: // Wasser — m³ total
        return { systempart: 4, valuetype: 22 }
    }
    // Other sensor types (temperature, pressure, …) don't have a
    // counter-style virtual mode — fall through to the physical defaults.
  }

  let systemPart = -1
  let valueType = -1

  switch (sensorType) {
    case 0: // power
      systemPart = 4; valueType = 3
      break
    case 1: // gas
      systemPart = 4; valueType = 13
      break
    case 2: // water
      systemPart = 4; valueType = 26
      break
    case 3: // heat
      systemPart = 4; valueType = 9
      break
    case 4: // temperature,
    case 12: //  warmwater,
    case 13: // cooling
      systemPart = 20; valueType = 16
      break
    case 5: // flooding
      systemPart = 4; valueType = 21
      break
    case 6: // pressure
      systemPart = 4; valueType = 24
      break
    case 8: // air
      systemPart = 4; valueType = 30
      break
    case 9: // generic aka collection
      systemPart = 4; valueType = 9 // FIXME!! only preliminary
      break
    case 10: // environmental
      systemPart = 28; valueType = 15
      break
    case 14: // brightness
      systemPart = 29; valueType = 21
      break
    default:
      break
  }
  return { systempart: systemPart, valuetype: valueType }
}

export function findFirstGlanceReading (sensor) {
  const datapackage = dataPackageForSensor(sensor)
  if (!datapackage) return null
  const pair = firstGlanceSystemPartAndType(sensor)
  return findSensorValueForSystemPartAndType(sensor, pair, datapackage)
}

export function highestTriggeredEventSeverity (sensor) {
  const events = sensor && sensor.triggeredEvents
  if (!events || events.length === 0) return 0
  let highest = 0
  for (const e of events) {
    const code = e && typeof e.severityCode === 'number' ? e.severityCode : 0
    if (code > highest) highest = code
  }
  return highest
}

// Max-silence window (minutes) for send-on-change sensors (CS-* v5),
// or null when the sensor is not known to be send-on-change. Priority:
// explicit json override `maxSilenceMin` > vt:36 in the latest package >
// sticky in-session observation. See utils/send-on-change.js.
export function maxSilenceMinutesForSensor (sensor, opts) {
  if (!sensor) return null
  const seenMap = resolveSeenMap(opts)
  const latest = dataPackageForSensor(sensor)
  const values = latest ? latest.values : null
  // remember the observation: a later package without vt:36 must not
  // make the sensor fall back to the strict model-table threshold
  if (values && values.some(v => v && v.type === 36) && !seenMap[sensor.id]) {
    seenMap[sensor.id] = true
  }
  return sendOnChange.maxSilenceMinutes(sensor.json, values, seenMap[sensor.id])
}

export function sensorIsOutdated (sensor, opts) { // see MER-91
  if (!sensor || !sensor.enabled) {
    return false
  }
  const recorded = recordedtime(sensor)
  if (!recorded) {
    return true
  }
  const now = resolveNow(opts)

  // Send-on-change sensors (CS-* v5) legitimately stay silent for up to
  // maxSilence minutes (forced report at the latest then) — the model
  // table below would flag them after ~3 minutes. 10 % tolerance plus
  // 2 min transmission slack: default 60 min => stale after 68 min.
  const silenceMin = maxSilenceMinutesForSensor(sensor, opts)
  if (silenceMin && !isVirtualSensor(sensor)) {
    const deltaMaxMs = silenceMin * 60 * 1.1 * 1000 + 120 * 1000
    return (now - moment(recorded).valueOf()) > deltaMaxMs
  }

  // Virtual sensors only honor updateinterval (ms). 0 = no notification
  // window configured → never outdated. No model-based defaults apply.
  if (isVirtualSensor(sensor)) {
    if (!sensor.updateinterval || sensor.updateinterval <= 0) {
      return false
    }
    const deltaMs = now - moment(recorded).valueOf()
    return deltaMs > sensor.updateinterval
  }

  let deltaMax = sensor.updateinterval / 1000
  if (deltaMax < 60) {
    deltaMax = 60
  }

  if (deltaMax === 60) {
    switch (sensor.model) {
      case 'CS-IRP':
      case 'CS-IRPL':
      case 'CS-IRW':
      case 'CS-HG':
        break
      case 'CS-HWL':
      case 'CS-HGL':
      case 'CS-HG_L':
      case 'CS-WGL':
      case 'CS-IRFL':
      case 'CS-IRWL':
        deltaMax = 3600
        break
      case 'CS-T':
      case 'CS-T_L':
      case 'CS-T1L':
      case 'CS-T2L':
        deltaMax = 600
        break
      case 'CS-RS2':
      case 'CS-RS4':
      case 'CS-IRH':
        break
      case 'CS-IRHL':
        deltaMax = 600
        break
      case 'CS-CT':
      case 'CS-IRF':
        break
      case 'CS-MBM':
      case 'CS-FLL':
      case 'CS-FML':
      case 'CS-AQL':
        deltaMax = 600
        break
      case 'CS-IRMB':
      case 'CS-IRMBL':
        deltaMax = 43200
        break
      default:
        break
    }
  }
  // we give a bit more time for sensors with 60s
  if (deltaMax === 60) {
    deltaMax = deltaMax * 3 * 1.1 * 1000
  } else {
    deltaMax = deltaMax * 2 * 1.1 * 1000
  }
  const delta = now - moment(recorded).valueOf() // milliseconds
  return delta > deltaMax
}

export function sensorAlertReason (sensor, opts) {
  if (!sensor.enabled) {
    return AlertReason.DEVICE_DISABLED
  }
  // Server-evaluated SensorEvents — same matching path as the notification
  // engine (SensorEventTriggerUtility). Highest severityCode wins:
  // 3 = critical, 2 = warning, 1 = info. Paused events still count toward
  // the bar color; they're only annotated in the alerts modal.
  const triggeredSeverity = highestTriggeredEventSeverity(sensor)
  if (triggeredSeverity >= 3) return AlertReason.SENSOR_ALERT
  if (triggeredSeverity >= 2) return AlertReason.SENSOR_WARNING

  const valueData = findFirstGlanceReading(sensor)

  if (valueData != null) {
    if (sensor.type === 5) { // .WARTERGUARD
      if (valueData.value > 0) {
        return AlertReason.SENSOR_ALERT
      }
    }
    if (sensor.type === 8) { // .AIR
      if (valueData.value >= 7) {
        return AlertReason.SENSOR_ALERT
      }
      if (valueData.value >= 5) {
        return AlertReason.SENSOR_WARNING
      }
    }
    const datapackage = dataPackageForSensor(sensor)
    const sensorAlertValue = findSensorValueForSystemPartAndType(sensor, { systempart: 30, valuetype: 31 }, datapackage)
    if (sensorAlertValue && sensorAlertValue.factor !== 0) {
      return AlertReason.SENSOR_WARNING
    }
    if (sensorIsOutdated(sensor, opts)) {
      return AlertReason.STALE_DATA
    }
    return AlertReason.NONE
  }
  if (sensorIsOutdated(sensor, opts)) {
    return AlertReason.STALE_DATA
  } else {
    return AlertReason.NONE
  }
}

export function sensorAlertStatus (sensor, opts) {
  const reason = sensorAlertReason(sensor, opts)
  switch (reason) {
    case AlertReason.SENSOR_ALERT:
      return AlertState.CRITICAL
    case AlertReason.STALE_DATA:
      return AlertState.INFO
    case AlertReason.BATTERY_LOW:
      return AlertState.WARNING
    case AlertReason.DEVICE_CONTACT:
      return AlertState.INFO
    case AlertReason.SENSOR_DISABLED:
      return AlertState.UNKNOWN
    case AlertReason.USAGE_LIMIT:
      return AlertState.WARNING
    case AlertReason.NO_SENSORDATA:
      return AlertState.WARNING
    case AlertReason.SENSOR_EVENT:
      return AlertState.UNKNOWN // getHighestWarningFromEvent(viewModel: viewModel)
    case AlertReason.NONE:
      return AlertState.OK
    default:
      return AlertState.INFO
  }
}

export function severityForReason (reason) {
  // Mirrors sensorAlertStatus() — keep in sync if mapping changes.
  switch (reason) {
    case AlertReason.SENSOR_ALERT: return 3
    case AlertReason.BATTERY_LOW:
    case AlertReason.USAGE_LIMIT:
    case AlertReason.NO_SENSORDATA:
    case AlertReason.SENSOR_WARNING: return 2
    case AlertReason.STALE_DATA:
    case AlertReason.DEVICE_CONTACT: return 1
    case AlertReason.SENSOR_DISABLED:
    case AlertReason.DEVICE_DISABLED:
    case AlertReason.SENSOR_EVENT: return 4
    default: return 1
  }
}

//
// deviceAlertReason without the presentation parts (icon / translated
// text stay in HomePage): reasons list + highest reason + the sensor
// that carries it.
//
export function deviceAlertReasonCore (device, opts) {
  if (!device.enabled) {
    return { reasons: [AlertReason.DEVICE_DISABLED], highestReason: AlertReason.DEVICE_DISABLED, sensorWithHighestAlert: null }
  }
  // Virtual devices have no heartbeat — their lastseen is just the
  // creation timestamp (same exception as deviceAlertCounts).
  if (device.virtual !== true) {
    let outdated = true
    const lastseen = device.lastseen
    if (lastseen) {
      outdated = isOutdated(moment(lastseen).valueOf(), opts)
    }
    if (outdated === true) {
      return { reasons: [AlertReason.DEVICE_CONTACT], highestReason: AlertReason.DEVICE_CONTACT, sensorWithHighestAlert: null }
    }
  }
  let sensorWithHighestAlert = null
  let highestReason = -1
  const reasons = []
  for (const sensor of device.sensors) {
    if (sensor.enabled === true) {
      const reason = sensorAlertReason(sensor, opts)
      if (reason > highestReason) {
        highestReason = reason
        sensorWithHighestAlert = sensor
      }
      if (reason !== AlertReason.NONE && reason !== AlertReason.DEVICE_DISABLED) {
        reasons.push(reason)
      }
    }
  }
  return { reasons, highestReason, sensorWithHighestAlert }
}

export function deviceAlertCounts (device, opts) {
  let sensorsWithAlerts = 0
  let sensorsWithCritical = 0
  let sensorsWithWarning = 0
  let sensorsWithInfo = 0
  let sensorsWithNone = 0
  let sensorWithUnknown = 0
  let sensorCount = 0
  for (const sensor of device.sensors) {
    if (sensor.enabled === true) {
      sensorCount += 1
      const result = sensorAlertStatus(sensor, opts)
      switch (result) {
        case -1: sensorWithUnknown += 1
          break
        case 0: sensorsWithNone += 1
          break
        case 1: sensorsWithInfo += 1
          break
        case 2: sensorsWithWarning += 1
          break
        case 3: sensorsWithCritical += 1
          break
        default:
          break
      }
    }
  }
  // Virtual devices have no heartbeat — their lastseen is just the
  // creation timestamp, which goes stale within minutes. Skip the
  // device-level outdated check and reflect the actual sensor mix.
  if (device.virtual === true) {
    sensorsWithAlerts = sensorsWithCritical + sensorsWithWarning + sensorsWithInfo
    return { sensorCount, sensorsWithAlerts, sensorsWithCritical, sensorsWithWarning, sensorsWithInfo, sensorsWithNone, sensorWithUnknown }
  }

  let outdated = true
  const lastseen = device.lastseen
  if (lastseen) {
    outdated = isOutdated(moment(lastseen).valueOf(), opts)
    // we handling the device like a sensor
    if (!outdated) {
      sensorsWithNone += 1
      sensorCount += 1
    } else {
      return { sensorCount: 1, sensorsWithAlerts: 1, sensorsWithCritical: 0, sensorsWithWarning: 1, sensorsWithInfo: 0, sensorsWithNone: 0, sensorWithUnknown: 0 }
    }
  }
  sensorsWithAlerts = sensorsWithCritical + sensorsWithWarning + sensorsWithInfo
  return { sensorCount, sensorsWithAlerts, sensorsWithCritical, sensorsWithWarning, sensorsWithInfo, sensorsWithNone, sensorWithUnknown }
}

export function buildGradientStyle (counts) {
  // Emit colour stops only; the component CSS renders them as either a
  // horizontal full-card gradient (classic) or a vertical left-strip
  // gradient (subtle).
  if (counts.sensorsWithAlerts === 0) {
    return 'rgba(127,232,127,1) 0%, rgba(127,232,127,1) 100%'
  }
  let stops = ''
  // Beginning
  if (counts.sensorsWithCritical > 0) {
    stops += 'rgba(228,0,0,1) 0%'
  } else if (counts.sensorsWithWarning > 0) {
    stops += 'rgba(255,128,0,1) 0%'
  } else if (counts.sensorsWithInfo > 0) {
    stops += 'rgba(255,181,3,1) 0%'
  } else if (counts.sensorsWithNone > 0) {
    stops += 'rgba(127,232,127,1) 0%'
  }
  // Mid
  if (counts.sensorsWithCritical > 0) {
    stops += ', rgba(255,128,0,1) ' + ((counts.sensorsWithCritical / counts.sensorCount) * 100) + '%'
  }
  if (counts.sensorsWithWarning > 0) {
    stops += ', rgba(255,181,3,1) ' + (((counts.sensorsWithCritical + counts.sensorsWithWarning) / counts.sensorCount) * 100) + '%'
  }
  if (counts.sensorsWithInfo > 0) {
    stops += ', rgba(127,232,127,1) ' + (((counts.sensorsWithCritical + counts.sensorsWithWarning + counts.sensorsWithInfo) / counts.sensorCount) * 100) + '%'
  }
  // End
  if (counts.sensorsWithNone > 0) {
    stops += ', rgba(127,232,127,1) 100%'
  } else {
    stops += ', rgba(255,181,3,1) 100%'
  }
  return stops
}

//
// ---- Dashboard status-tile aggregators -------------------------------
//
// severity semantics (matches facilitySeverityForSort in HomePage):
// only 1..3 rank as urgency; DEVICE_DISABLED & co. rank 0. A fully
// disabled entity is reported as severity 4 (grey, intentional state).
//

function severityRankForReasons (reasons) {
  let max = 0
  for (const r of reasons || []) {
    const sev = severityForReason(r)
    const rank = (sev === 3 || sev === 2 || sev === 1) ? sev : 0
    if (rank > max) max = rank
  }
  return max
}

function uniqueSortedReasons (reasons) {
  const unique = Array.from(new Set(reasons || []))
  return unique.sort((a, b) => severityRankForReasons([b]) - severityRankForReasons([a]))
}

// -> { severity: 0..3 | 4 (disabled), reasons: [AlertReason...] }
export function statusForDevice (device, opts) {
  if (!device || device.enabled === false) {
    return { severity: 4, reasons: [AlertReason.DEVICE_DISABLED] }
  }
  const core = deviceAlertReasonCore(device, opts)
  return { severity: severityRankForReasons(core.reasons), reasons: uniqueSortedReasons(core.reasons) }
}

// -> { severity: 0..3 | 4 (disabled), reasons: [AlertReason...] }
export function statusForFacility (facility, opts) {
  if (!facility || facilityIsDisabled(facility)) {
    return { severity: 4, reasons: [AlertReason.DEVICE_DISABLED] }
  }
  const reasons = []
  for (const device of facility.devices) {
    if (device.enabled === true) {
      reasons.push(...deviceAlertReasonCore(device, opts).reasons)
    }
  }
  return { severity: severityRankForReasons(reasons), reasons: uniqueSortedReasons(reasons) }
}

// entries: [{ severity, ... }] -> counts + worst severity for the tile
// border. `worst` ignores disabled entries unless everything is disabled.
export function statusSummary (entries) {
  const summary = { ok: 0, info: 0, warning: 0, critical: 0, disabled: 0, worst: 0 }
  for (const entry of entries || []) {
    switch (entry.severity) {
      case 4: summary.disabled += 1
        break
      case 3: summary.critical += 1
        break
      case 2: summary.warning += 1
        break
      case 1: summary.info += 1
        break
      default: summary.ok += 1
        break
    }
  }
  if (summary.critical > 0) summary.worst = 3
  else if (summary.warning > 0) summary.worst = 2
  else if (summary.info > 0) summary.worst = 1
  else if (summary.ok === 0 && summary.disabled > 0) summary.worst = 4
  return summary
}
